import * as React from 'react';
import RequestStatus from '../store/schema/RequestStatus';
import {
    SuggestionsStore,
    IPeopleFloatingPickerProps,
} from 'office-ui-fabric-react/lib/FloatingPicker';
import getSuggestionProps from '../utils/getSuggestionProps';
import { ITheme } from '@uifabric/styling';
import LocationCommonWellItemViewState from '../store/schema/LocationCommonWellItemViewState';
import LocationPickerState from '../store/schema/LocationPickerState';
import { SearchContext } from '../store/schema/LocationPickerState';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { generateLocationWellColor } from './generateLocationWellStyle';
import LocationSuggestionItem from '../components/LocationSuggestionItem';
import findLocation from '../utils/findLocation';
import { owaDate } from 'owa-datetime';

/**
 * Location floating picker props
 */
export default function getLocationPickerProps(
    getPickerState: () => LocationPickerState,
    onPickerStateChanged: (state: Partial<LocationPickerState>) => void,
    getSearchContext: () => SearchContext,
    theme: ITheme,
    colorScheme: ColorScheme,
    roomFreeBusyEnabled: boolean,
    disableUseThisLocation: boolean,
    zeroQueryDisabled: boolean
): IPeopleFloatingPickerProps {
    return {
        suggestionsStore: new SuggestionsStore<LocationCommonWellItemViewState>(),
        onRenderSuggestionsItem: onRenderSuggestion(roomFreeBusyEnabled, colorScheme),
        onResolveSuggestions: getQuerySuggestions(
            getPickerState(),
            getSearchContext,
            onPickerStateChanged
        ),
        onZeroQuerySuggestion: zeroQueryDisabled
            ? null
            : getZeroQuerySuggestions(getPickerState(), getSearchContext, onPickerStateChanged),
        calloutWidth: 300,
        resolveDelay: 150,
        pickerSuggestionsProps: getSuggestionProps(
            getPickerState().queryString,
            getPickerState().findLocationRequestStatus,
            getPickerState().suggestionSet.length,
            theme,
            colorScheme,
            disableUseThisLocation
        ),
    };
}

function onRenderSuggestion(
    roomFreeBusyEnabled: boolean,
    colorScheme: ColorScheme
): (props: LocationCommonWellItemViewState) => JSX.Element {
    return function(props: LocationCommonWellItemViewState): JSX.Element {
        const { persona } = props;

        return (
            <LocationSuggestionItem
                key={persona.personaControlId}
                locationResult={persona}
                locationWellColor={generateLocationWellColor(colorScheme)}
                roomFreeBusyEnabled={roomFreeBusyEnabled}
            />
        );
    };
}

function getQuerySuggestions(
    pickerState: LocationPickerState,
    getSearchContext: () => SearchContext,
    onPickerStateChanged: (state: Partial<LocationPickerState>) => void
): (
    queryString: string,
    currentItems: LocationCommonWellItemViewState[]
) => Promise<LocationCommonWellItemViewState[]> {
    return function(queryString: string, currentItems: LocationCommonWellItemViewState[]) {
        return getSuggestions(queryString, pickerState, getSearchContext(), onPickerStateChanged);
    };
}

function getZeroQuerySuggestions(
    pickerState: LocationPickerState,
    getSearchContext: () => SearchContext,
    onPickerStateChanged: (state: Partial<LocationPickerState>) => void
): (currentItems: LocationCommonWellItemViewState[]) => Promise<LocationCommonWellItemViewState[]> {
    return function(currentItems: LocationCommonWellItemViewState[]) {
        return getSuggestions(
            '' /* queryString */,
            pickerState,
            getSearchContext(),
            onPickerStateChanged
        );
    };
}

function getSuggestions(
    queryString: string,
    pickerState: LocationPickerState,
    searchContext: SearchContext,
    onPickerStateChanged: (state: Partial<LocationPickerState>) => void
): Promise<LocationCommonWellItemViewState[]> {
    if (queryString.trim() != '' && queryString === pickerState.queryString) {
        // Return the existing suggestions if non-zero-input query string doesn't change
        return Promise.resolve(pickerState.suggestionSet);
    }

    const { event, timeSlot, searchMode, currentDevicePosition } = searchContext;

    const curFindLocationRequestTimestamp = owaDate();
    onPickerStateChanged({
        queryString: queryString.trim(),
        findLocationRequestStatus: RequestStatus.Pending,
    });

    return findLocation(
        queryString,
        event ? event.RequiredAttendees.map(attendee => attendee.Mailbox.EmailAddress) : [],
        event ? event.OptionalAttendees.map(attendee => attendee.Mailbox.EmailAddress) : [],
        timeSlot,
        currentDevicePosition,
        searchMode
    ).then(locationResults => {
        if (curFindLocationRequestTimestamp < pickerState.displayedFindLocationRequestTimestamp) {
            // Skip processing responses which are older than the displayed suggestions.
            // This is used to prevent the old request's response coming back later than the newer one's, which could mess up the suggestion set.
            // Also we don't check queryString here to improve the client responsiveness, otherwise it is highly likely we never show any suggestions until the user stops typing.
            return pickerState.suggestionSet;
        }

        const locationSuggestions = locationResults.map(location => {
            return {
                persona: location,
                text: location.displayName,
                isValid: true,
                isSelected: false,
                isContextMenuOpen: false,
            } as LocationCommonWellItemViewState;
        });

        onPickerStateChanged({
            findLocationRequestStatus: RequestStatus.Complete,
            displayedFindLocationRequestTimestamp: curFindLocationRequestTimestamp,
            suggestionSet: locationSuggestions,
        });
        return locationSuggestions;
    });
}
